import React from 'react';
import { RA2NDatePicker } from '@/components/organisms/RA2NDatePicker';
import AppTextField from '@/components/atoms/AppTextField';
import AppTextArea from '@/components/atoms/AppTextArea';
import type {
  PendingVisitData,
  VisitClinicDayVisit,
  VisitClinicPriceData,
  VisitClinicRezept,
  VisitClinicRezeptField,
} from '@/types/visitClinic.types';
import dayjs from 'dayjs';
import { parseYearmonthParam } from '@/utils/dateConverter';
import { SERVICE_TYPE_R2 } from '@/constants/serviceType';
import {
  CONSENT_DIFFERENCE_REASON_MAX_LENGTH,
  PRICE_SECTION_ELEMENT_ID,
  consentDifferenceReasonElementId,
  rezeptPriceBlockElementId,
} from './consentDifferenceReason';

interface Props {
  serviceType: string;
  rezepts: VisitClinicRezept[];
  visits: Record<string, VisitClinicDayVisit>;
  pending: Record<string, PendingVisitData>;
  initialInspectionPrices?: { single: number; combined: number };
  onRezeptChange: (rezeptId: number, field: VisitClinicRezeptField, value: string) => void;
  /**
   * R2 (HIT-15106, BD 1.3.2 S25 2026/11/05) — tables whose 「当月施術内容が同意書と異なる理由」
   * still has to be filled in: their box gets a red border until it holds text.
   */
  reasonRequiredRezeptIds?: number[];
}

/**
 * QC request (2026/11/05): a box left holding only spaces / line breaks (full-width included)
 * is emptied when focus leaves it, so it does not look filled while counting as empty.
 */
function clearBlankOnBlur(
  value: string,
  rezeptId: number,
  field: VisitClinicRezeptField,
  onRezeptChange: Props['onRezeptChange'],
) {
  if (value !== '' && value.trim() === '') onRezeptChange(rezeptId, field, '');
}

/**
 * 料金｜コメント section — horizontal price table matching legacy A2 layout.
 * Columns: 訪問施術料1術/2術/往療料/特別地域加算 (令和6年10月改正法以降のみ適用)
 * 明細書発行加算 + 一部負担金: 2026/7療養費改定 (HIT-14773, UAT Sprint6 No.204)
 *
 * Header color scheme mirrors A2 .money-grid:
 *   初検 #CCFFFF | はり #FFFF99 | きゅう #CCFFCC | はり・きゅう #CCCCFF
 *   電   #FFCC99 | 往診系 #EAEAEA | 実日数・合計 #fff
 */
const VisitClinicPriceSection: React.FC<Props> = ({
  serviceType,
  rezepts,
  visits,
  pending,
  initialInspectionPrices,
  onRezeptChange,
  reasonRequiredRezeptIds = [],
}) => {
  // The id lets the 詳細編集 reminder scroll here (HIT-15106), also when the month has no rezept.
  if (rezepts.length === 0) return <div id={PRICE_SECTION_ELEMENT_ID} className="mt-2" />;

  const isR2 = serviceType.toUpperCase() === SERVICE_TYPE_R2;

  return (
    <div id={PRICE_SECTION_ELEMENT_ID} className="mt-2">
      {rezepts.map(rezept => {
        const pd = isR2
          ? rezept.price_data
          : applyPendingInitialFee(
              rezept.price_data,
              rezept.id,
              visits,
              pending,
              initialInspectionPrices
            );
        const isPublished = rezept.all_status !== 0;
        const isReturn = !!rezept.is_return;
        // 提出年月 editable only when NOT in return processing AND rezept not yet published
        const canEditYearmonth = !isReturn && !isPublished;

        // Build column definitions (with header bg colors matching A2)
        const cols = buildCols(isR2);

        const treatmentYm = String(rezept.treatement_yearmonth ?? '');
        const submissionStartMonth =
          treatmentYm.length >= 6
            ? dayjs(`${treatmentYm.slice(0, 4)}-${treatmentYm.slice(4, 6)}-01`).add(1, 'month')
            : null;

        // Default 提出年月 = treatement_yearmonth + 1 month (BD §8.4).
        // A2 parity (schedule.blade.php:1310-1311): when 返戻処理中 AND
        // rezept_yearmonth is null → value empty so placeholder 返戻処理中
        // is visible (do NOT fall back to treatement+1).
        const effectiveYearmonth = (() => {
          if (isReturn && !rezept.rezept_yearmonth) {
            return undefined;
          }
          const ym = String(rezept.rezept_yearmonth ?? '');
          if (ym.length >= 6) {
            return `${ym.slice(0, 4)}-${ym.slice(4, 6)}`;
          }
          if (submissionStartMonth?.isValid()) {
            return submissionStartMonth.format('YYYY-MM');
          }
          return undefined;
        })();

        return (
          <div
            key={rezept.id}
            id={rezeptPriceBlockElementId(rezept.id)}
            className="mb-4 rounded bg-gray-300 p-2.5"
          >
            {/* Insurance info line */}
            <div className="mb-2 flex flex-wrap items-center gap-2 text-sm">
              {rezept.insurance_label && (
                <span>{rezept.insurance_label}</span>
              )}
              {isPublished && (
                <span className="font-bold text-red-600">※レセプト発行済</span>
              )}
              <div className="ml-auto flex items-center gap-1 text-sm">
                <span className="whitespace-nowrap">提出年月</span>
                <RA2NDatePicker
                  picker="month"
                  value={effectiveYearmonth}
                  disabled={!canEditYearmonth}
                  startDate={
                    submissionStartMonth?.isValid()
                      ? submissionStartMonth.startOf('month').toDate()
                      : undefined
                  }
                  placeholder={isReturn && !rezept.rezept_yearmonth ? '返戻処理中' : '年月指定'}
                  onChange={val => {
                    if (val) {
                      // RA2NDatePicker returns wareki string (e.g. 令和08年04月) → convert to YYYYMM
                      const iso = parseYearmonthParam(val); // "2026-04"
                      const ym = iso.replace('-', ''); // "202604"
                      onRezeptChange(rezept.id, 'rezept_yearmonth', ym);
                    }
                  }}
                  className="w-32"
                />
              </div>
            </div>

            {/* Horizontal price table (matches legacy money_table) */}
            <div className="overflow-x-auto">
              <table className="table-fixed border-collapse text-base min-w-[600px] w-full">
                <thead>
                  <tr>
                    {cols.map(c => (
                      <th
                        key={c.key}
                        className={`border border-gray-300 px-1 py-1 text-center whitespace-nowrap ${c.widthClass}`}
                        style={{ backgroundColor: c.bgColor }}
                      >
                        {c.label}
                      </th>
                    ))}
                    <th
                      className="border border-gray-300 px-1 py-1 text-center whitespace-nowrap bg-white w-14"
                      rowSpan={2}
                    >
                      実日数
                    </th>
                    <th
                      className="border border-gray-300 px-1 py-1 text-center whitespace-nowrap bg-white w-[74px]"
                      rowSpan={2}
                    >
                      {isR2 ? '合計' : '合計金額'}
                    </th>
                    <th
                      className="border border-gray-300 px-1 py-1 text-center whitespace-nowrap bg-white w-[74px]"
                      rowSpan={2}
                    >
                      一部負担金
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {/* 金額 row */}
                  <tr>
                    {cols.map(c => (
                      <td
                        key={c.key}
                        className="border border-gray-300 px-1 py-1 text-right bg-white"
                      >
                        {Number(pd[c.amountKey as keyof typeof pd] ?? 0).toLocaleString()}円
                      </td>
                    ))}
                    <td
                      className="border border-gray-300 px-1 py-1 text-right bg-white"
                      rowSpan={2}
                    >
                      {pd.operation_day_count}日
                    </td>
                    <td
                      className="border border-gray-300 px-1 py-1 text-right font-semibold bg-white"
                      rowSpan={2}
                    >
                      {pd.total_price.toLocaleString()}円
                    </td>
                    <td
                      className="border border-gray-300 px-1 py-1 text-right font-semibold bg-white"
                      rowSpan={2}
                      data-testid={`part-of-charge-${rezept.id}`}
                    >
                      {(pd.part_of_charge ?? 0).toLocaleString()}円
                    </td>
                  </tr>
                  {/* 件数 row */}
                  <tr>
                    {cols.map(c => (
                      <td
                        key={c.key}
                        className="border border-gray-300 px-1 py-1 text-right bg-white"
                      >
                        {Number(pd[c.countKey as keyof typeof pd] ?? 0)}回
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>

            {/* 当月摘要欄 (summary-row layout matching legacy) */}
            {!isR2 && (
              <div className="mt-2 flex items-center gap-2">
                <label className="shrink-0 whitespace-nowrap text-base text-gray-700">
                  当月レセプト<br />摘要欄内容
                </label>
                {summaryField(rezept, isPublished, onRezeptChange)}
              </div>
            )}

            {/* R2: 当月摘要欄 + 「当月施術内容が同意書と異なる理由」 right under it (HIT-15106,
                BD 1.2.2 §8.18). A two-column grid so both boxes start at the same x. */}
            {isR2 && (
              <div className="mt-2 grid grid-cols-[auto_minmax(0,1fr)] items-center gap-x-2 gap-y-2">
                <label className="whitespace-nowrap text-base text-gray-700">
                  当月レセプト<br />摘要欄内容
                </label>
                {summaryField(rezept, isPublished, onRezeptChange)}
                <label
                  htmlFor={consentDifferenceReasonElementId(rezept.id)}
                  className="whitespace-nowrap text-base text-gray-700"
                >
                  当月施術内容が<br />同意書と異なる理由
                </label>
                <AppTextArea
                  id={consentDifferenceReasonElementId(rezept.id)}
                  // Same rule as 当月摘要欄: locked once the rezept is issued (status ≠ 0).
                  className={`min-w-0${isPublished ? '' : ' bg-white'}`}
                  value={rezept.consent_difference_reason ?? ''}
                  maxLength={CONSENT_DIFFERENCE_REASON_MAX_LENGTH}
                  autoSize={{ minRows: 1, maxRows: 4 }}
                  disabled={isPublished}
                  status={reasonRequiredRezeptIds.includes(rezept.id) ? 'error' : undefined}
                  onChange={e =>
                    onRezeptChange(rezept.id, 'consent_difference_reason', e.target.value)
                  }
                  onBlur={e =>
                    clearBlankOnBlur(e.target.value, rezept.id, 'consent_difference_reason', onRezeptChange)
                  }
                />
              </div>
            )}

            {/* 令和8年法改正 No 8 — customer-worded note (2026-09), red, inside the
                料金｜コメント panel. 合計金額 carries the ×0.8 while the columns above keep
                full price, so without this line the total simply looks wrong.

                Replaces the old 「※この画面では料金は加算されません。」 note: since the
                同 customer decision the screen DOES fold in the 明細書発行加算 and this
                逓減, so that sentence had become false. */}
            {pd.is_concentration_target && (
              <p className="mt-2 text-sm font-medium text-red-600">
                ※ 集中率90％以上の施設等入居者の為、合計金額は逓減率80％の金額です。
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
};

function summaryField(
  rezept: VisitClinicRezept,
  isPublished: boolean,
  onRezeptChange: Props['onRezeptChange'],
) {
  return (
    <AppTextField
      // BD §8.17: disabled once the rezept is issued (status ≠ 0). readOnly kept the
      // white enabled look, so users could not tell the field was locked.
      className={`min-w-0 flex-1 h-9${isPublished ? '' : ' bg-white'}`}
      value={rezept.summary}
      maxLength={1000}
      disabled={isPublished}
      onChange={e => onRezeptChange(rezept.id, 'summary', e.target.value)}
      onBlur={e => clearBlankOnBlur(e.target.value, rezept.id, 'summary', onRezeptChange)}
      placeholder="摘要を入力"
    />
  );
}

type ColDef = {
  key: string;
  label: string;
  amountKey: string;
  countKey: string;
  widthClass: string;
  bgColor: string;
};

// 令和8年法改正 No 5 — 10円 per billing day; already inside 合計金額, now shown on its own.
const MEISAI_COL: ColDef = {
  key: 'meisai',
  label: '明細書発行加算',
  amountKey: 'statement_issuance_fee_total',
  countKey: 'statement_issuance_fee_count',
  widthClass: 'w-[106px]',
  bgColor: '#EAEAEA',
};

function buildCols(isR2: boolean): ColDef[] {
  if (isR2) {
    // Column order and labels follow BD SCR-163 §8:
    //   通所 / 訪問施術料 / 温罨法 / 温罨・電療 / 変形徒手 / 特別地域加算 / 往療料 / 施術報告書交付料
    // 明細書発行加算 sits right before 施術報告書交付料, as on A2 (HIT-14773).
    // The two 温罨法 columns carried A2's wording (電(鍼・温) / 電療) after the port; R2
    // bills them as 温罨法 (hot_treatment_price, 同意書 hot_treatment_type_id = 1) and
    // 温罨・電療 (electric_treatment_price, type = 2), which is what the amounts already were.
    return [
      { key: 'r2_commute',  label: '通所',             amountKey: 'r2_commute',             countKey: 'r2_commute_count',             widthClass: 'w-[54px]',  bgColor: '#FFFF99' },
      { key: 'r2_visit',    label: '訪問施術料',       amountKey: 'r2_visit_treatment',     countKey: 'r2_visit_treatment_count',     widthClass: 'w-[88px]',  bgColor: '#EAEAEA' },
      { key: 'r2_hot',      label: '温罨法',           amountKey: 'r2_hot_fomentation',     countKey: 'r2_hot_fomentation_count',     widthClass: 'w-[64px]',  bgColor: '#FFCC99' },
      { key: 'r2_electric', label: '温罨・電療',       amountKey: 'r2_electric',            countKey: 'r2_electric_count',            widthClass: 'w-[84px]',  bgColor: '#FFCC99' },
      { key: 'r2_ortho',    label: '変形徒手',         amountKey: 'r2_orthomorphia',        countKey: 'r2_orthomorphia_count',        widthClass: 'w-[76px]',  bgColor: '#CCFFCC' },
      { key: 'sa',          label: '特別地域加算',     amountKey: 'special_area_addition',  countKey: 'special_area_addition_count',  widthClass: 'w-[84px]',  bgColor: '#EAEAEA' },
      { key: 'hc',          label: '往療料',           amountKey: 'house_call',             countKey: 'house_call_count',             widthClass: 'w-[60px]',  bgColor: '#EAEAEA' },
      MEISAI_COL,
      { key: 'tr',          label: '施術報告書交付料', amountKey: 'treatment_report_price', countKey: 'treatment_report_count',       widthClass: 'w-[106px]', bgColor: '#EAEAEA' },
    ];
  }

  return [
    { key: 'initial',     label: '初検',            amountKey: 'initial',                countKey: 'initial_count',               widthClass: 'w-[54px]',  bgColor: '#CCFFFF' },
    { key: 'acupuncture', label: 'はり',             amountKey: 'acupuncture',            countKey: 'acupuncture_count',           widthClass: 'w-[54px]',  bgColor: '#FFFF99' },
    { key: 'moxibustion', label: 'きゅう',           amountKey: 'moxibustion',            countKey: 'moxibustion_count',           widthClass: 'w-[54px]',  bgColor: '#CCFFCC' },
    { key: 'both',        label: 'はり・きゅう',     amountKey: 'both',                   countKey: 'both_count',                  widthClass: 'w-20',      bgColor: '#CCCCFF' },
    { key: 'electric',    label: '電(鍼・温)',       amountKey: 'electric',               countKey: 'electric_count',              widthClass: 'w-[68px]',  bgColor: '#FFCC99' },
    { key: 'vt1',         label: '訪問施術料1術',    amountKey: 'visit_treatment1',       countKey: 'visit_treatment1_count',      widthClass: 'w-[90px]',  bgColor: '#EAEAEA' },
    { key: 'vt2',         label: '訪問施術料2術',    amountKey: 'visit_treatment2',       countKey: 'visit_treatment2_count',      widthClass: 'w-[90px]',  bgColor: '#EAEAEA' },
    { key: 'hc',          label: '往療料',           amountKey: 'house_call',             countKey: 'house_call_count',            widthClass: 'w-[60px]',  bgColor: '#EAEAEA' },
    { key: 'sa',          label: '特別地域加算',     amountKey: 'special_area_addition',  countKey: 'special_area_addition_count', widthClass: 'w-[84px]',  bgColor: '#EAEAEA' },
    // Customer mock (UAT No.204): between 特別地域加算 and 施術報告書交付料.
    MEISAI_COL,
    { key: 'tr',          label: '施術報告書交付料',  amountKey: 'treatment_report_price', countKey: 'treatment_report_count',      widthClass: 'w-[106px]', bgColor: '#EAEAEA' },
  ];
}

export default VisitClinicPriceSection;

function initialFeeUnit(
  isInitial: number | null | undefined,
  isAcupuncture: number | null | undefined,
  isMoxibustion: number | null | undefined,
  prices?: { single: number; combined: number }
): number {
  if (!prices || Number(isInitial ?? 0) !== 1) return 0;
  return Number(isAcupuncture ?? 0) === 1 && Number(isMoxibustion ?? 0) === 1
    ? prices.combined
    : prices.single;
}

function applyPendingInitialFee(
  priceData: VisitClinicPriceData,
  rezeptId: number,
  visits: Record<string, VisitClinicDayVisit>,
  pending: Record<string, PendingVisitData>,
  initialInspectionPrices?: { single: number; combined: number }
): VisitClinicPriceData {
  let amountDelta = 0;
  let countDelta = 0;

  Object.entries(pending).forEach(([date, draft]) => {
    if (!draft.is_dirty) return;

    const day = String(Number(date.slice(-2)));
    const original = visits[day];
    if (!original || Number(original.rezept_id ?? 0) !== rezeptId) return;

    const oldAmount = initialFeeUnit(
      original.is_initial_inspection_fee,
      original.is_acupuncture,
      original.is_moxibustion,
      initialInspectionPrices
    );
    const newAmount = draft.is_deleted
      ? 0
      : initialFeeUnit(
          draft.is_initial_inspection_fee,
          draft.is_acupuncture,
          draft.is_moxibustion,
          initialInspectionPrices
        );

    amountDelta += newAmount - oldAmount;
    countDelta += (newAmount > 0 ? 1 : 0) - (oldAmount > 0 ? 1 : 0);
  });

  if (amountDelta === 0 && countDelta === 0) {
    return priceData;
  }

  const totalPrice = Math.max(0, priceData.total_price + amountDelta);

  return {
    ...priceData,
    initial: Math.max(0, priceData.initial + amountDelta),
    initial_count: Math.max(0, priceData.initial_count + countDelta),
    total_price: totalPrice,
    // Keep 一部負担金 in step with the moved 合計金額 until 登録 reloads it from the server
    // (same expression as VisitClinicService::withPartOfCharge / the レセプト PDF).
    part_of_charge: Math.ceil(totalPrice * ((priceData.patient_cost_rate ?? 0) / 10)),
  };
}
